import { OverflowMenu } from "@carbon/react";
import { ServerError } from "@flaminem/utils";
import { FC, useState } from "react";

import { AuthorizedMenu, AuthorizedOverflowMenuItem } from "src/authentication/AuthorizedMenu";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { ModalTransition } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer";
import { useEnableBlacklistElementMutation } from "src/graphql/anti-fraud/queries/blacklists/mutations/EnableBlacklistElementMutation";
import { useT } from "src/internationalization";
import { BlacklistElementRecord } from "src/pages/Blacklists/blacklist.mapping";

import { BlacklistElementModal } from "./BlacklistElementModal";
import { ConfirmDeactivateBlacklistModal } from "./ConfirmDeactivateBlacklistModal";

export type BlacklistActionsProps = {
  blacklistElementRecord: BlacklistElementRecord;
  onStatusChange: () => void;
  onElementUpdated: () => void;
};

export const BlacklistActions: FC<BlacklistActionsProps> = ({
  blacklistElementRecord,
  onStatusChange,
  onElementUpdated,
}) => {
  const t = useT();
  const { showErrorToast, showSuccessToast } = useToast();
  const [blacklistToDisable, setBlacklistToDisable] = useState<BlacklistElementRecord | null>(null);
  const [blacklistToUpdate, setBlacklistToUpdate] = useState<BlacklistElementRecord | null>(null);

  const { performMutation: enableBlacklist } = useEnableBlacklistElementMutation({
    onSuccess: () => {
      showSuccessToast(
        t("page.blacklists.table.action.update.status.success.message", {
          status: t("page.blacklists.table.status.inactive").toLowerCase(),
        })
      );

      onStatusChange();
    },
    onError: (e: ServerError) => {
      showErrorToast(e.message);
    },
  });

  return (
    <>
      <div>
        <AuthorizedMenu
          menuItemsPermissions={[
            { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.DISABLE] },
            { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.EDIT] },
          ]}
        >
          <OverflowMenu flipped={true}>
            <AuthorizedOverflowMenuItem
              requiredPermissions={[
                { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.DISABLE] },
              ]}
              itemText={
                <div>
                  {blacklistElementRecord.status
                    ? t("page.blacklists.table.menu.disable")
                    : t("page.blacklists.table.menu.activate")}
                </div>
              }
              onClick={() => {
                if (blacklistElementRecord.status)
                  return setBlacklistToDisable(blacklistElementRecord);
                return enableBlacklist({ id: blacklistElementRecord.id });
              }}
            />
            {blacklistElementRecord.status && (
              <AuthorizedOverflowMenuItem
                requiredPermissions={[
                  { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.EDIT] },
                ]}
                itemText={<div>{t("general.actions.edit")}</div>}
                onClick={() => {
                  setBlacklistToUpdate(blacklistElementRecord);
                }}
              />
            )}
          </OverflowMenu>
        </AuthorizedMenu>
      </div>
      <ModalTransition>
        {blacklistToDisable && (
          <ConfirmDeactivateBlacklistModal
            blacklistElementRecord={blacklistToDisable}
            onClose={() => setBlacklistToDisable(null)}
            onConfirm={() => {
              onStatusChange();
              setBlacklistToDisable(null);
            }}
          />
        )}
        {blacklistToUpdate && (
          <BlacklistElementModal
            onClose={() => setBlacklistToUpdate(null)}
            onSuccess={() => {
              setBlacklistToUpdate(null);
              onElementUpdated();
            }}
            item={blacklistToUpdate}
          />
        )}
      </ModalTransition>
    </>
  );
};
